import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, X, Loader2, CheckCircle2, ImagePlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { SIZES, FRAMES } from "../data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_BYTES = 5 * 1024 * 1024; // 5MB

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  description: "",
  sizeId: "m",
  frameId: "negro",
  photo: null, // { name, type, dataUrl }
};

export const CustomOrderDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  const updateField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Por favor sube una imagen (JPG/PNG/WebP).");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("La imagen no puede superar 5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      updateField("photo", {
        name: file.name,
        type: file.type,
        dataUrl: reader.result,
      });
    };
    reader.readAsDataURL(file);
  };

  const validate = () => {
    if (!form.fullName.trim()) return "Nombre es requerido";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Email inválido";
    if (form.phone.replace(/\D/g, "").length < 7) return "Teléfono inválido";
    if (!form.photo) return "Sube una foto para el cuadro";
    if (!form.description.trim()) return "Cuéntanos un poco sobre tu idea";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);

    setSubmitting(true);
    try {
      const payload = {
        fullName: form.fullName,
        email: form.email,
        phone: form.phone,
        description: form.description,
        sizeId: form.sizeId,
        sizeLabel: SIZES.find((s) => s.id === form.sizeId).label,
        frameId: form.frameId,
        frameLabel: FRAMES.find((f) => f.id === form.frameId).label,
        photo_name: form.photo.name,
        photo_type: form.photo.type,
        photo_base64: form.photo.dataUrl,
      };
      const { data } = await axios.post(`${API}/custom-orders`, payload);
      setDone(data);
    } catch (e) {
      console.error(e);
      toast.error("No pudimos enviar tu solicitud. Inténtalo de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = (o) => {
    if (!o) {
      onOpenChange(false);
      setTimeout(() => {
        if (done) {
          setDone(null);
          setForm(initialForm);
        }
      }, 300);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="custom-order-dialog"
        aria-describedby={undefined}
        className="max-w-2xl bg-slate-50 border-slate-200 p-0 overflow-hidden max-h-[92vh] flex flex-col"
      >
        {done ? (
          <div
            data-testid="custom-order-success"
            className="flex flex-col items-center text-center px-8 py-12"
          >
            <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-5">
              <CheckCircle2 className="h-9 w-9 text-green-600" strokeWidth={1.75} />
            </div>
            <DialogHeader className="space-y-2">
              <DialogTitle className="font-serif text-3xl text-slate-900">
                ¡Solicitud recibida!
              </DialogTitle>
              <DialogDescription className="text-base text-slate-600 max-w-md">
                Recibimos tu foto. Te contactaremos a{" "}
                <span className="font-medium">{done.email}</span> con una
                cotización en menos de 24 horas.
              </DialogDescription>
            </DialogHeader>
            <Button
              onClick={() => handleClose(false)}
              data-testid="custom-order-success-close"
              className="mt-8 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8"
            >
              Listo
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-200">
              <DialogTitle className="font-serif text-2xl text-slate-900">
                Tu cuadro personalizado
              </DialogTitle>
              <DialogDescription className="text-slate-500">
                Sube una foto y nosotros la convertimos en un cuadro enmarcado.
              </DialogDescription>
            </DialogHeader>

            <form
              onSubmit={handleSubmit}
              className="flex-1 overflow-y-auto px-8 py-6 space-y-5"
            >
              {/* Upload */}
              <div>
                <Label className="text-slate-700">Foto del cuadro</Label>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="custom-photo-input"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                {form.photo ? (
                  <div
                    data-testid="custom-photo-preview"
                    className="mt-1.5 relative rounded-xl overflow-hidden border border-slate-200 bg-white"
                  >
                    <img
                      src={form.photo.dataUrl}
                      alt="preview"
                      className="w-full h-56 object-contain bg-slate-100"
                    />
                    <button
                      type="button"
                      onClick={() => updateField("photo", null)}
                      data-testid="custom-photo-remove"
                      className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 text-slate-700 hover:text-[#E85A3C] flex items-center justify-center shadow"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <div className="px-4 py-2 text-xs text-slate-600 border-t border-slate-100 truncate">
                      {form.photo.name}
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    data-testid="custom-photo-button"
                    className="mt-1.5 w-full h-44 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#E85A3C] hover:bg-[#E85A3C]/5 transition-all duration-300 flex flex-col items-center justify-center gap-2 text-slate-500"
                  >
                    <ImagePlus className="h-7 w-7" strokeWidth={1.5} />
                    <span className="text-sm font-medium">
                      Toca para subir una foto
                    </span>
                    <span className="text-xs text-slate-400">
                      JPG/PNG/WebP · máx. 5MB
                    </span>
                  </button>
                )}
              </div>

              {/* Personal info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="cName" className="text-slate-700">
                    Nombre
                  </Label>
                  <Input
                    id="cName"
                    data-testid="custom-name"
                    value={form.fullName}
                    onChange={(e) => updateField("fullName", e.target.value)}
                    placeholder="Tu nombre"
                    className="mt-1.5 bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="cEmail" className="text-slate-700">
                    Email
                  </Label>
                  <Input
                    id="cEmail"
                    data-testid="custom-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    placeholder="tucorreo@email.co"
                    className="mt-1.5 bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="cPhone" className="text-slate-700">
                    Teléfono
                  </Label>
                  <Input
                    id="cPhone"
                    data-testid="custom-phone"
                    value={form.phone}
                    onChange={(e) => updateField("phone", e.target.value)}
                    placeholder="3001234567"
                    className="mt-1.5 bg-white"
                  />
                </div>
              </div>

              {/* Size + frame */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-700">Tamaño</Label>
                  <select
                    data-testid="custom-size"
                    value={form.sizeId}
                    onChange={(e) => updateField("sizeId", e.target.value)}
                    className="mt-1.5 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  >
                    {SIZES.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-slate-700">Marco</Label>
                  <select
                    data-testid="custom-frame"
                    value={form.frameId}
                    onChange={(e) => updateField("frameId", e.target.value)}
                    className="mt-1.5 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  >
                    {FRAMES.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="cDesc" className="text-slate-700">
                  Cuéntanos sobre tu idea
                </Label>
                <Textarea
                  id="cDesc"
                  data-testid="custom-description"
                  value={form.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  placeholder="¿Es un regalo? ¿Algún detalle especial?"
                  rows={3}
                  className="mt-1.5 bg-white"
                />
              </div>
            </form>

            <div className="px-8 py-5 border-t border-slate-200 bg-white">
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                data-testid="custom-submit"
                className="w-full h-12 rounded-full bg-[#E85A3C] hover:bg-[#D3492D] text-white text-base font-medium shadow-lg shadow-[#E85A3C]/30"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Enviar solicitud
                  </>
                )}
              </Button>
              <p className="text-[11px] text-slate-400 text-center mt-2">
                Te respondemos con cotización en menos de 24h.
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
